import { useCallback, useEffect, useState } from 'react';
import { fetchSignupExperienceConfig } from '../services/auth';

const DEFAULT_CONFIG = {
  freeSignupTokens: 0,
  historyCostTokens: 1,
  updatedAt: null,
};

const normalizeInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseConfig = (payload = {}, fallback = DEFAULT_CONFIG) => ({
  freeSignupTokens: normalizeInteger(payload.freeSignupTokens, fallback.freeSignupTokens),
  historyCostTokens: normalizeInteger(payload.historyCostTokens, fallback.historyCostTokens),
  updatedAt: payload.updatedAt || fallback.updatedAt || null,
});

const readBootstrapConfig = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_CONFIG;
  }

  const bootstrapBilling = window.__APP_CONFIG__?.billing || window.__APP_CONFIG__?.marketing || {};
  return {
    ...DEFAULT_CONFIG,
    ...parseConfig(bootstrapBilling),
  };
};

export function useSignupExperienceConfig(options = {}) {
  const { fetchOnMount = true } = options;
  const [config, setConfig] = useState(readBootstrapConfig);
  const [loading, setLoading] = useState(Boolean(fetchOnMount));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSignupExperienceConfig();
      setConfig(parseConfig(data));
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fetchOnMount) {
      refresh();
    }
  }, [fetchOnMount, refresh]);

  return {
    config,
    loading,
    error,
    refresh,
  };
}

export default useSignupExperienceConfig;
